import { Component, type ErrorInfo, type ReactNode } from 'react';

import { messages } from '@/shared/constants/messages';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Substitui a tela padrão. Recebe a função de nova tentativa. */
  fallback?: (retry: () => void) => ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Último anteparo da interface: uma exceção de render não pode deixar o
 * voluntário diante de uma tela branca sem saída (RNF-U03).
 *
 * Não substitui tratamento de erro de query — erro de rede e erro de RPC são
 * tratados na tela, com a mensagem traduzida por `shared/lib/errors.ts`.
 * Aqui é só o que ninguém previu.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Sem Sentry na v1 (tech-stack / architecture §9): o detalhe fica no
    // console do navegador, que é onde o desenvolvedor solo vai procurar.
    console.error('[erro de render]', error, info.componentStack);
  }

  private readonly retry = (): void => {
    this.setState({ hasError: false });
  };

  override render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.retry);

    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-16 text-center">
        <p className="text-sm text-muted-foreground">{messages.errors.unexpected}</p>
        <button
          type="button"
          onClick={this.retry}
          className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {messages.actions.retry}
        </button>
      </div>
    );
  }
}
